import { useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import { usePostLedgerCorrection } from "./use-post-ledger-correction";
import { resolvePayoutError } from "./resolve-payout-error";
import { toNegativeCentsString } from "./correction-amount";
import { formatMoney, moneyFromCentsString } from "./money";
import { CORRECTION_REASON_MAX_LENGTH, CORRECTION_REASON_MIN_LENGTH } from "./constants";
import {
  LEDGER_CORRECTION_TYPES,
  type LedgerCorrectionType,
  type ManualLedgerEntry,
} from "./types";

const LABEL = "font-sans text-sm font-medium text-foreground";
const SELECT =
  "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50";

export function LedgerCorrectionsPage() {
  const { t } = useTranslation();
  const [type, setType] = useState<LedgerCorrectionType>("refund");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("");
  const [reason, setReason] = useState("");
  const [venueId, setVenueId] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [subscriptionId, setSubscriptionId] = useState("");
  const [stripeReference, setStripeReference] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ManualLedgerEntry | null>(null);
  // One idempotency key per logical correction intent — held in state so a
  // retry after an ambiguous failure reuses the SAME key (the server can then
  // dedupe instead of double-posting). Rotated only after a confirmed success,
  // where the next correction is a genuinely new intent.
  const [idempotencyKey, setIdempotencyKey] = useState(() => uuidv4());

  const post = usePostLedgerCorrection();

  const trimmedCurrency = currency.trim();
  const amountCents = toNegativeCentsString(amount, trimmedCurrency);
  const reasonValid = reason.trim().length >= CORRECTION_REASON_MIN_LENGTH;
  const canSubmit =
    amountCents !== null && trimmedCurrency.length > 0 && reasonValid && !post.isPending;

  const optional = (value: string): string | undefined => {
    const v = value.trim();
    return v.length > 0 ? v : undefined;
  };

  const handleSubmit = () => {
    if (!canSubmit || amountCents === null) return;
    setError(null);
    setResult(null);
    post.mutate(
      {
        type,
        amountCents,
        currency: trimmedCurrency.toUpperCase(),
        reason: reason.trim(),
        venueId: optional(venueId),
        propertyId: optional(propertyId),
        subscriptionId: optional(subscriptionId),
        stripeReference: optional(stripeReference),
        idempotencyKey,
      },
      {
        onSuccess: (entry) => {
          setResult(entry);
          // Clear the inputs so a hurried operator can't accidentally re-submit
          // the same correction (a fresh key over still-populated fields would
          // be a genuine second posting). The `result` banner stays as proof.
          setAmount("");
          setCurrency("");
          setReason("");
          setVenueId("");
          setPropertyId("");
          setSubscriptionId("");
          setStripeReference("");
          // Next correction is a new intent → fresh key.
          setIdempotencyKey(uuidv4());
        },
        onError: (err) => setError(resolvePayoutError(err, t)),
      },
    );
  };

  return (
    <div data-testid="ledger-corrections-screen" className="max-w-2xl space-y-6">
      <header className="space-y-2">
        <p className="brand-eyebrow">{t("finance.corrections.eyebrow")}</p>
        <h1 className="text-3xl">{t("finance.corrections.title")}</h1>
        <p className="font-sans text-base font-normal text-muted-foreground">
          {t("finance.corrections.subtitle")}
        </p>
      </header>

      <p className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 font-sans text-sm text-amber-700">
        {t("finance.corrections.noStripeWarning")}
      </p>

      <div className="space-y-4 rounded-lg border border-border bg-card p-6">
        <div className="space-y-1.5">
          <label htmlFor="correction-type" className={LABEL}>
            {t("finance.corrections.type")}
          </label>
          <select
            id="correction-type"
            data-testid="correction-type-select"
            value={type}
            onChange={(e) => setType(e.target.value as LedgerCorrectionType)}
            className={SELECT}
            disabled={post.isPending}
          >
            {LEDGER_CORRECTION_TYPES.map((value) => (
              <option key={value} value={value}>
                {t(`finance.corrections.types.${value}`)}
              </option>
            ))}
          </select>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label htmlFor="correction-amount" className={LABEL}>
              {t("finance.corrections.amount")}
            </label>
            <Input
              id="correction-amount"
              data-testid="correction-amount-input"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputMode="decimal"
              placeholder="0.00"
              disabled={post.isPending}
            />
            <p className="font-sans text-xs text-muted-foreground">
              {t("finance.corrections.amountHint")}
            </p>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="correction-currency" className={LABEL}>
              {t("finance.corrections.currency")}
            </label>
            <Input
              id="correction-currency"
              data-testid="correction-currency-input"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              maxLength={3}
              placeholder="USD"
              autoComplete="off"
              disabled={post.isPending}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <label htmlFor="correction-reason" className={LABEL}>
            {t("finance.corrections.reason")}
          </label>
          <Textarea
            id="correction-reason"
            data-testid="correction-reason-textarea"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            minLength={CORRECTION_REASON_MIN_LENGTH}
            maxLength={CORRECTION_REASON_MAX_LENGTH}
            rows={2}
            placeholder={t("finance.corrections.reasonPlaceholder")}
            disabled={post.isPending}
          />
          <p className="font-sans text-xs text-muted-foreground">
            {t("finance.corrections.reasonHint")}
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <OptionalRef
            id="correction-venue"
            testId="correction-venue-input"
            label={t("finance.corrections.venueId")}
            value={venueId}
            onChange={setVenueId}
            disabled={post.isPending}
          />
          <OptionalRef
            id="correction-property"
            testId="correction-property-input"
            label={t("finance.corrections.propertyId")}
            value={propertyId}
            onChange={setPropertyId}
            disabled={post.isPending}
          />
          <OptionalRef
            id="correction-subscription"
            testId="correction-subscription-input"
            label={t("finance.corrections.subscriptionId")}
            value={subscriptionId}
            onChange={setSubscriptionId}
            disabled={post.isPending}
          />
          <OptionalRef
            id="correction-stripe-ref"
            testId="correction-stripe-ref-input"
            label={t("finance.corrections.stripeReference")}
            value={stripeReference}
            onChange={setStripeReference}
            disabled={post.isPending}
          />
        </div>

        {error && (
          <p
            data-testid="correction-error"
            role="alert"
            className="font-sans text-sm font-medium text-destructive"
          >
            {error}
          </p>
        )}

        {result && (
          <p
            data-testid="correction-result"
            className="rounded-md border border-emerald-300/40 bg-emerald-50 px-3 py-2 font-sans text-sm text-emerald-800"
          >
            {t("finance.corrections.success", {
              type: t(`finance.corrections.entryTypeLabel.${result.entryType}`, {
                defaultValue: result.entryType,
              }),
              amount: formatMoney(moneyFromCentsString(result.amountCents, result.currency)),
              currency: result.currency,
            })}
          </p>
        )}

        {amountCents !== null && trimmedCurrency.length > 0 && (
          <p
            data-testid="correction-preview"
            className="font-sans text-sm font-medium text-foreground"
          >
            {t("finance.corrections.preview", {
              amount: formatMoney(moneyFromCentsString(amountCents, trimmedCurrency)),
            })}
          </p>
        )}

        <Button
          data-testid="submit-correction-button"
          variant="destructive"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {post.isPending ? t("finance.corrections.pending") : t("finance.corrections.submit")}
        </Button>
      </div>
    </div>
  );
}

interface OptionalRefProps {
  readonly id: string;
  readonly testId: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly disabled: boolean;
}

function OptionalRef({ id, testId, label, value, onChange, disabled }: OptionalRefProps) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className={LABEL}>
        {label}
      </label>
      <Input
        id={id}
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="off"
        disabled={disabled}
      />
    </div>
  );
}
